import { zodResolver } from '@hookform/resolvers/zod'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { parsePhoneNumber } from 'react-phone-number-input'

import Fieldset from '@/components-latest/layout/Fieldset'
import Button from '@/components-latest/primitives/Button'
import {
  AvatarInputField,
  Form,
  InputField
} from '@/components-latest/shared/Form'
import { editStoreInfoFormSchema } from '@/ducks/account/account.schema'
import { IStoreProfileData } from '@/ducks/account/account.types'

type Props = {
  initialStoreInfo?: IStoreProfileData
  closeEditForm: () => void
}

import { PlusIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import PhoneInput from '@/components-latest/primitives/Controls/PhoneInput'
import ConfirmationModal from '@/components-latest/shared/Modals/ConfirmationModal'
import { mapStoreInfoToForm } from '@/components-latest/ui/AccountPage/EditStoreInfoForm/helpers'
import {
  useEditStoreInfoMutation,
  useUploadStoreImageMutation
} from '@/ducks/account/api/account.api'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './EditStoreInfoForm.module.scss'
import RemovableFieldSet from './RemovableFieldSet'

export type IEditStoreFormData = z.infer<typeof editStoreInfoFormSchema>

const initialFormData: IEditStoreFormData = {
  website: '',
  support: {
    number: null,
    code: null
  },
  inReview: false,
  genre: '',
  facebook: '',
  email2: null,
  email1: '',
  address: '',
  avg_ratings: 0,
  store_img_name: '',
  store_name: '',
  phone1: '',
  phone2: null,
  phone3: null
}

type IPhoneFields = 'phone1' | 'phone2' | 'phone3'
type IRemovablePhoneField = Exclude<IPhoneFields, 'phone1'>
const removablePhones: IRemovablePhoneField[] = ['phone2', 'phone3']

const EditStoreInfoForm = ({ initialStoreInfo, closeEditForm }: Props) => {
  const { t } = useTranslation()

  const form = useForm<IEditStoreFormData>({
    resolver: zodResolver(editStoreInfoFormSchema),
    mode: 'onSubmit',
    defaultValues: initialFormData,
    reValidateMode: 'onSubmit',
    shouldFocusError: false
  })

  const {
    handleSubmit,
    clearErrors,
    reset,
    control,
    watch,
    setError,
    setValue,
    formState: { errors, isDirty }
  } = form

  useEffect(() => {
    if (!initialStoreInfo) return
    reset(mapStoreInfoToForm(initialStoreInfo))
  }, [initialStoreInfo, reset])

  useEffect(
    () => () => {
      clearErrors()
      reset(initialFormData)
    },
    [clearErrors, reset]
  )

  const storeNameValue = watch('store_name')

  // PHONES ---------------------------------
  const phone1 = watch('phone1')
  const phone2 = watch('phone2')
  const phone3 = watch('phone3')
  const phoneValues = {
    phone1,
    phone2,
    phone3
  }

  const defaultPhoneCountry = useMemo(() => {
    return phone1 ? parsePhoneNumber(phone1)?.country || '' : ''
  }, [phone1])

  const areAllPhonesVisible = [phone1, phone2, phone3].every(
    phone => phone !== null
  )

  const onPhoneChangeCreator =
    (phoneName: IPhoneFields) => (phoneValue: string) => {
      setValue(phoneName, phoneValue, { shouldDirty: true })
    }

  const onPhoneRemove = (phoneField: IRemovablePhoneField) => {
    if (phoneField === 'phone2' && phoneValues['phone3'] !== null) {
      setValue('phone2', phoneValues['phone3'], { shouldDirty: true })
      setValue('phone3', null, { shouldDirty: true })
      return
    }
    setValue(phoneField, null, { shouldDirty: true })
  }

  const onPhoneAdd = () => {
    const emptyPhoneFieldName = removablePhones.find(
      phoneField => phoneValues[phoneField] === null
    )

    const defaultNumber = phone1 ? parsePhoneNumber(phone1) : null
    const defaultCountryCode = defaultNumber
      ? `+${defaultNumber.countryCallingCode}`
      : ''

    if (emptyPhoneFieldName) {
      setValue(emptyPhoneFieldName, defaultCountryCode, { shouldDirty: true })
    }
  }

  // EMAILS -------------------------------------------
  const email2 = watch('email2')
  const onEmailRemove = () => {
    setValue('email2', null, { shouldDirty: true })
  }
  const onEmailAdd = () => {
    setValue('email2', '', { shouldDirty: true })
  }

  const storeId = useSelector(selectStoreId)
  const dispatch = useDispatch()
  const [editStoreInfo, { isLoading }] = useEditStoreInfoMutation()
  const [uploadStoreImage, { isLoading: isImageUploading }] =
    useUploadStoreImageMutation()

  const onSubmit = useCallback(
    (formData: IEditStoreFormData) => {
      if (!storeId) return

      const editFormFunc = (imageUrl: string | null) => {
        editStoreInfo({
          formData: { ...formData, store_img_name: imageUrl },
          store_id: storeId
        })
          .unwrap()
          .then(({ message, status }: { message: string; status: string }) => {
            dispatch(
              addSystemNotification({
                message: message,
                intent: status === '1' ? 'success' : 'error',
                duration: 3000
              })
            )
            closeEditForm()
          })
      }

      if (!(formData.store_img_name instanceof File)) {
        editFormFunc(formData.store_img_name)
        return
      }

      const imageFormData = new FormData()
      imageFormData.append('store_img_profile', formData.store_img_name)

      uploadStoreImage({ formData: imageFormData })
        .unwrap()
        .then(({ message, status, store_img_name }) => {
          dispatch(
            addSystemNotification({
              message: message,
              intent: status === '1' ? 'success' : 'error',
              duration: 3000
            })
          )

          const updatedImage =
            status === '1' && store_img_name
              ? store_img_name
              : initialStoreInfo?.store_img_name || ''

          editFormFunc(updatedImage)
        })
    },
    [
      editStoreInfo,
      dispatch,
      storeId,
      closeEditForm,
      uploadStoreImage,
      initialStoreInfo
    ]
  )

  return (
    <ContentBlock>
      <Form {...form}>
        <form className={Styles.form} onSubmit={handleSubmit(onSubmit)}>
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label=""
            className={Styles.avatarField}>
            <AvatarInputField
              isLoading={isImageUploading}
              disabled={isImageUploading || isLoading}
              name="store_img_name"
              className={Styles.input}
              control={control}
              onChange={value => {
                setValue('store_img_name', value, { shouldDirty: true })
              }}
              placeholderString={storeNameValue || ''}
            />
          </Fieldset>
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label={t('account.edit-info.store-name-label')}>
            <InputField
              data-testid="edit-store-form-store_name"
              name="store_name"
              intent={errors.store_name?.message ? 'negative' : 'default'}
              placeholder={t('account.edit-info.store-name-placeholder')}
              className={Styles.input}
              onFocus={() => {
                setError('store_name', { message: '' })
              }}
              control={control}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label={t('account.edit-info.phone-label')}>
            <PhoneInput
              value={phone1}
              data-testid="edit-store-form-phone1"
              zIndex={13}
              disabled
              className={Styles.input}
              onChange={onPhoneChangeCreator('phone1')}
            />
          </Fieldset>
          {removablePhones.map(phoneFieldName => {
            const value = phoneValues[phoneFieldName]

            return value !== null ? (
              <RemovableFieldSet
                disabled={isLoading}
                onDeleteClick={() => onPhoneRemove(phoneFieldName)}
                key={phoneFieldName}>
                <PhoneInput
                  value={value}
                  className={Styles.input}
                  defaultCountry={defaultPhoneCountry}
                  data-testid={`edit-store-form-${phoneFieldName}`}
                  disabled={isLoading}
                  zIndex={phoneFieldName === 'phone2' ? 12 : 11}
                  onChange={onPhoneChangeCreator(phoneFieldName)}
                />
              </RemovableFieldSet>
            ) : null
          })}
          {!areAllPhonesVisible && (
            <Button
              onClick={onPhoneAdd}
              icon={<PlusIcon />}
              type="button"
              disabled={isLoading}
              className={Styles.addButton}
              variant="transparent">
              {t('account.edit-info.add-phone-number')}
            </Button>
          )}
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label={t('account.edit-info.email-label')}>
            <InputField
              data-testid="edit-store-form-email1"
              name="email1"
              intent={errors.email1?.message ? 'negative' : 'default'}
              control={control}
              onFocus={() => {
                setError('email1', { message: '' })
              }}
              placeholder={t('account.edit-info.email-placeholder')}
              className={Styles.input}
              disabled={isLoading}
            />
          </Fieldset>
          {email2 !== null ? (
            <RemovableFieldSet
              disabled={isLoading}
              onDeleteClick={onEmailRemove}>
              <InputField
                data-testid="edit-store-form-email2"
                name="email2"
                intent="default"
                placeholder="example@yassir.com"
                className={Styles.input}
                control={control}
                disabled={isLoading}
              />
            </RemovableFieldSet>
          ) : (
            <Button
              onClick={onEmailAdd}
              type="button"
              className={Styles.addButton}
              disabled={isLoading}
              icon={<PlusIcon />}
              variant="transparent">
              {t('account.edit-info.add-email')}
            </Button>
          )}
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label={t('account.edit-info.facebook-label')}>
            <InputField
              data-testid="edit-store-form-facebook"
              name="facebook"
              className={Styles.input}
              intent="default"
              control={control}
              placeholder={t('account.edit-info.facebook-placeholder')}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset variant="nextBody" weight="semiBold" label="Instagram">
            <InputField
              data-testid="edit-store-form-inst"
              name="genre"
              className={Styles.input}
              intent="default"
              control={control}
              placeholder="Instagram page link"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset
            variant="nextBody"
            weight="semiBold"
            label={t('account.edit-info.website-label')}>
            <InputField
              data-testid="edit-store-form-website"
              name="website"
              className={Styles.input}
              intent="default"
              placeholder={t('account.edit-info.website-placeholder')}
              control={control}
              disabled={isLoading}
            />
          </Fieldset>
          <ConfirmationModal
            title={t('account.edit-info.confirm-title')}
            description={t('account.edit-info.confirm-description')}
            confirmButtonText={t('account.edit-info.confirm-submit')}
            onConfirm={handleSubmit(onSubmit)}>
            <Button
              className={Styles.submitButton}
              loading={isLoading || isImageUploading}
              disabled={isLoading || isImageUploading || !isDirty}
              variant="primary"
              type="button">
              {t('account.edit-info.save-changes')}
            </Button>
          </ConfirmationModal>
        </form>
      </Form>
    </ContentBlock>
  )
}

export default EditStoreInfoForm
